import { useInfiniteQuery } from '@tanstack/react-query'
import { Empty, Flex, Select, Spin } from 'antd'
import { SelectProps } from 'antd/lib'

import { useClassifierServiceGetListResultValueOptionsEndpointKey } from 'src/api/queries'
import {
  ClassifierService,
  GetCommonClassifierElementResponse
} from 'src/api/requests'
import { DEFAULT_PER_PAGE } from 'src/app-constants'
import { useLocales } from 'src/locales'

export const ValueOptionInfiniteSelect = (props: SelectProps) => {
  const { currentLang, t } = useLocales()

  const { data, fetchNextPage, isFetching } = useInfiniteQuery({
    queryKey: [useClassifierServiceGetListResultValueOptionsEndpointKey],
    queryFn: async ({ pageParam = 1 }: { pageParam?: number }) => {
      const response =
        await ClassifierService.getListResultValueOptionsEndpoint(
          pageParam,
          DEFAULT_PER_PAGE
        )

      return {
        data: response.data,
        total_pages: response.totalPages ?? 0,
        current_page: pageParam
      }
    },
    getNextPageParam: (lastPage: {
      data: GetCommonClassifierElementResponse[] | undefined
      total_pages: number
      current_page: number
    }) =>
      lastPage?.current_page < lastPage?.total_pages
        ? lastPage.current_page + 1
        : undefined,
    initialPageParam: 1
  })

  const selectOptions = data?.pages.flatMap(
    (page) =>
      page.data?.map((item: GetCommonClassifierElementResponse) => ({
        value: item?.id,
        label: item?.translation?.[currentLang?.value]
      })) || []
  )

  return (
    <Select
      allowClear
      filterOption={false}
      options={selectOptions}
      loading={isFetching}
      showSearch={false}
      placeholder={t('common.select_from_list')}
      onPopupScroll={(e) => {
        const target = e.target as HTMLElement
        if (target.scrollTop + target.offsetHeight === target.scrollHeight)
          fetchNextPage()
      }}
      notFoundContent={
        isFetching ? (
          <Flex justify="center">
            <Spin spinning />
          </Flex>
        ) : (
          <Empty />
        )
      }
      {...props}
    />
  )
}
